// =============================================================================
// StaffField.tsx
// Type-to-search staff input used in MobileAddOrder. Wraps an `.input` field
// with an absolutely-positioned dropdown of matching directory entries. Click
// (mousedown) on a row populates the parent's value via `onChange` and closes
// the list.
// =============================================================================

import React, { useMemo, useState } from 'react';

import type { StaffMember } from '../../../lib/types';

// ── TYPES ────────────────────────────────────────────────────────────

interface StaffFieldProps {
  value: string;
  onChange: (next: string) => void;
  staffDirectory: StaffMember[];
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export function StaffField({ value, onChange, staffDirectory }: StaffFieldProps) {
  const [showList, setShowList]   = useState(false);
  const [filter,   setFilter]     = useState('');

  const matches = useMemo(
    () => staffDirectory
      .filter(s => s.active !== false)
      .filter(s => !filter.trim() || s.name.toLowerCase().includes(filter.toLowerCase()))
      .slice(0, 5),
    [staffDirectory, filter],
  );

  return (
    <div style={{ position: 'relative' }}>
      <label className="field-label">Ordered for</label>
      <input
        value={value}
        onChange={e => { onChange(e.target.value); setFilter(e.target.value); setShowList(true); }}
        onFocus={() => setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 200)}
        placeholder="Staff name…"
        className="input"
      />
      {showList && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, zIndex: 10, maxHeight: 200, overflow: 'auto',
          marginTop: 2,
        }}>
          {matches.map(s => (
            <button
              key={s.id}
              onMouseDown={() => { onChange(s.name); setShowList(false); }}
              style={{
                display: 'block', width: '100%', minHeight: 44,
                padding: '10px 12px', fontSize: 'var(--m-text-meta)', border: 'none',
                background: 'none', textAlign: 'left' as const, cursor: 'pointer',
                fontFamily: 'inherit', color: 'var(--text)',
                borderBottom: '0.5px solid var(--border)',
              }}
            >{s.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}
